import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from '@bloo-team/ui'
import { SlidersHorizontal } from '@bloo-team/ui/icons'

/**
 * Column visibility menu, in the pager row. Popover mechanics match the
 * host pages' other menus; the list is the frozen column set for THIS
 * task, so it never reflows while paging.
 */
export function ColumnsMenu({
  columns,
  hidden,
  onToggle,
}: {
  columns: string[]
  hidden: ReadonlySet<string>
  onToggle: (column: string) => void
}) {
  const [open, setOpen] = useState(false)
  // The filter, cleared on every open: with 30+ columns the list is unusable
  // without one, and a stale filter from last time reads as missing columns.
  const [q, setQ] = useState('')
  const rootRef = useRef<HTMLSpanElement>(null)
  const idBase = useId()

  useEffect(() => {
    if (!open) return
    // Focus lands in the filter — with 30+ columns, typing IS the next act.
    rootRef.current?.querySelector<HTMLInputElement>('.q-menu-search')?.focus()
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <span className="q-history" ref={rootRef}>
      <button
        type="button"
        className="pager-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => {
          setQ('')
          setOpen((o) => !o)
        }}
      >
        <Icon icon={SlidersHorizontal} size={11} /> columns
        {hidden.size ? ` (${columns.length - hidden.size}/${columns.length})` : ''}
      </button>
      {open ? (
        <div className="q-history-menu q-columns-menu" aria-label="Visible columns">
          <input
            className="q-menu-search"
            aria-label="Filter columns"
            placeholder="filter columns…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {columns
            .filter((c) => c.toLowerCase().includes(q.trim().toLowerCase()))
            .map((c, i) => (
            <label key={c} className="q-column-item" htmlFor={`${idBase}-${i}`}>
              <input
                id={`${idBase}-${i}`}
                type="checkbox"
                aria-label={`Show column ${c}`}
                checked={!hidden.has(c)}
                onChange={() => onToggle(c)}
              />
              <span className="mono">{c}</span>
            </label>
          ))}
        </div>
      ) : null}
    </span>
  )
}
